<template>
    <div class="datepicker-trigger-container" tabindex="-1">
        <label class="sr-only visually-hidden" for="arrivalDateFocus">{{ config.datepickerConfig.i18n.select_arrival || 'Select your arrival' }}</label>
        <input type="date" @keydown.enter.prevent="" @change="flexibleMonthLabel = null"
               class="sr-only visually-hidden sr-only-focusable visually-hidden-focusable" v-model="arrival"
               id="arrivalDateFocus">

        <label v-if="isRangeMode" class="sr-only visually-hidden" for="departureDateFocus">{{ config.datepickerConfig.i18n.select_departure || 'Select your departure' }}</label>
        <input v-if="isRangeMode" type="date" @keydown.enter.prevent="" @change="flexibleMonthLabel = null"
               class="sr-only visually-hidden sr-only-focusable visually-hidden-focusable" v-model="departure"
               id="departureDateFocus">

        <div
            class="datepicker-main-wrapper"
            :class="{ 'datepicker-with-pricing': enablePricing, 'datepicker-single-mode': !isRangeMode }">
            <input
                class="form-control"
                type="text"
                id="trigger"
                :aria-label="config.datepickerConfig.i18n.selected_date || 'Your selected date'"
                aria-haspopup="dialog"
                :aria-expanded="isDatepickerOpen ? 'true' : 'false'"
                :value="flexibleMonthLabel || dateDisplay(arrival, departure)"
                readonly
            >
        </div>
        <input type="hidden" name="arrival" :value="arrival">
        <input type="hidden" v-if="isRangeMode" name="departure" :value="departure">
    </div>
</template>

<script>
import '../sass/hotel-datepicker.scss';
import HotelDatepicker from 'hotel-datepicker';
import HotelDatePickerService from '../Services/HotelDatePickerService';
import 'hotel-datepicker/dist/css/hotel-datepicker.css';
import spacetime from "spacetime";
import {formatToISO, arabicDigits, attachCurrency} from "../Utils/utils";
import {renderComponentToHtml} from "../Utils/renderComponentToHtml";
import CalendarLegend from "./CalendarLegend.vue";
import CalendarTooltipList from "./CalendarTooltipList.vue";

export default {
    name: "datepicker",
    props: {
        config: {required: true, type: Object},
    },
    data() {
        const { datepickerConfig } = this.config;

        return {
            dateDisplay: datepickerConfig.dateDisplay,
            flexibleMonthLabel: null,
            calendarObserver: null,
            arrival: datepickerConfig.arrival,
            departure: datepickerConfig.mode !== 'range' ? datepickerConfig.arrival : datepickerConfig.departure,
            selectedHotel: datepickerConfig.selectedHotel,
            isDatepickerOpen: false,
            service: new HotelDatePickerService({
                onUpdate: (data) => {
                    this.$set(this.config.datepickerConfig, 'calendarData', { ...data });
                    this.reinitDatePicker();
                    this.validateSelection();
                    this.updateCurrencyNoticeInDOM();
                    if (this.arrival) {
                        const dep = !this.isRangeMode
                            ? spacetime(this.arrival).add(this.config.datepickerConfig.numNights || 1, 'day').format('iso').substring(0, 10)
                            : this.departure;
                        if (dep) this.updateFromPriceInDOM(this.arrival, dep, data);
                    }
                },
                onLoadingStart: (keys) => {
                    const loadingMonths = keys.map(key => key.split('-').slice(1, 3).join('-'));
                    const currentData = { ...(this.config.datepickerConfig.calendarData || {}) };
                    Object.keys(currentData).forEach(date => {
                        if (loadingMonths.some(month => date.startsWith(month))) {
                            delete currentData[date];
                        }
                    });
                    this.$set(this.config.datepickerConfig, 'calendarData', currentData);
                    this.reinitDatePicker();
                },
            }),
        }
    },
    watch: {
        'config.datepickerConfig.selectedHotel'(newHotelId) {
            this.selectedHotel = newHotelId;
            this.config.datepickerConfig.calendarData = {};
            this.service.currencyEntityForNotice = null;
            this.clearFromPrice();
            this.updateCurrencyNoticeInDOM();
            this.updateErrorVisibility(true);
            this.updateLegendVisibility();
        },
        'config.datepickerConfig.numNights'() {
            if (!this.isRangeMode) {
                this.addMinStayDataToCells();
            }
        },
    },
    created() {
        this.refreshTimeout = null;
        this._afterCloseHandler = () => { this.isDatepickerOpen = false; };
        this._preClickMinStayHandler = this.updateMinStayForEffectiveArrival.bind(this);
        this._eventTooltipClickHandler = this.handleEventTooltipClick.bind(this);
        this._tooltipHtmlCache = new Map();
        this._flexibleDatesHandler = (event) => {
            const { arrival, departure } = event.detail || {};
            if (!arrival) {
                return;
            }
            this.arrival = arrival;
            this.departure = this.isRangeMode ? (departure || arrival) : this.arrival;
            this.flexibleMonthLabel = this.formatFlexibleMonthLabel(arrival);
            this.$emit('change', { arrival: this.arrival, departure: this.departure });
        };
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
        this.initPicker();
        this.setupListenersForMonthButtons();
        this.initCalendarObserver();
        document.addEventListener('click', this._preClickMinStayHandler, true);
        document.addEventListener('click', this._eventTooltipClickHandler, true);
        const triggerInput = document.getElementById('trigger');
        if (triggerInput) {
            triggerInput.addEventListener('afterClose', this._afterCloseHandler);
            triggerInput.addEventListener('flexibleDatesSelected', this._flexibleDatesHandler);
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);

        if (this._monthButtonHandler) {
            document.removeEventListener('click', this._monthButtonHandler);
        }

        document.removeEventListener('click', this._preClickMinStayHandler, true);
        document.removeEventListener('click', this._eventTooltipClickHandler, true);

        if (this.calendarObserver) {
            this.calendarObserver.disconnect();
        }

        if (this.refreshTimeout) {
            cancelAnimationFrame(this.refreshTimeout);
        }

        const triggerInput = document.getElementById('trigger');
        if (triggerInput) {
            triggerInput.removeEventListener('afterClose', this._afterCloseHandler);
            triggerInput.removeEventListener('flexibleDatesSelected', this._flexibleDatesHandler);
        }
    },
    computed: {
        isRangeMode() {
            return this.config.datepickerConfig.mode === 'range';
        },
        enablePricing() {
            return this.config.datePickerAvailabilityDisplayVersion === 2;
        },
    },
    methods: {
        formatFlexibleMonthLabel(arrival) {
            const [year, month] = arrival.split('-');
            const monthNames = (window.spacetimeTranslations && window.spacetimeTranslations.months.short) || [];
            const monthName = monthNames[parseInt(month, 10) - 1] || '';

            return arabicDigits(`${monthName} ${year}`.trim());
        },
        getPickerConfig() {
            const config = this.config.datepickerConfig;
            const maxDays = this.config.datepickerConfig.maxDays || 365;
            const maxDate = spacetime().add(maxDays, 'day').format('iso').split('T')[0];

            return {
                moveBothMonths: true,
                format: 'YYYY-MM-DD',
                startDate: config.minDate ? new Date(config.minDate) : new Date(),
                endDate: maxDate,
                disabledDates: [],
                noCheckInDates: [],
                noCheckOutDates: [],
                showTopbar: true,
                topbarPosition: 'bottom',
                i18n: config.i18n,
                minNights: config.minNights ?? 1,
                enableCheckout: true,
                startOfWeek: config.startOfWeek,
                getValue: () => {
                    return (this.arrival && this.departure) ? `${this.arrival} - ${this.departure}` : '';
                },
                hoveringTooltip: function(nights, startTime, hoverTime) {
                    const textNight = config.i18n.night || 'night';
                    const textNights = config.i18n.nights || 'nights';
                    const nightText = nights === 1 ? textNight : textNights;
                    let displayNights =  arabicDigits(nights.toString());

                    return `${displayNights} ${nightText}`;
                },
                setValue: () => {},
                extraDayText: (date) => {
                    if (!this.enablePricing) {
                        return '<div class="custom-datepicker__tooltip"></div>';
                    }

                    return ' <div class="custom-datepicker__tooltip"></div><span class="calendar-price small-text"></span>';
                },
                onOpenDatepicker: () => {
                    this.isDatepickerOpen = true;
                    const topbar = document.querySelector('.datepicker__topbar');
                    const customHTML = this.getCustomHTML();

                    if (topbar && !document.getElementById('custom-html') && customHTML) {
                        const customDiv = document.createElement('div');
                        customDiv.id = 'custom-html';
                        customDiv.innerHTML = customHTML;
                        topbar.appendChild(customDiv);
                    }

                    this.updateLegendVisibility();
                    this.validateSelection();
                    this.refreshAvailability(true);
                    this.translateCalendarToArabic();

                    if (!this.isRangeMode && !this.enablePricing) {
                        this.$nextTick(() => this.matchDatepickerWidthToRow());
                    }
                },
                onDayClick: (event) => {
                    const datePicker = window.hotelDatePicker;
                    const calendarData = config.calendarData || {};

                    if (!datePicker.start && !datePicker.end) {
                        this.reinitDatePicker();
                        this.validateSelection();
                        if (this.arrival) {
                            const dep = !this.isRangeMode
                                ? spacetime(this.arrival).add(config.numNights || 1, 'day').format('iso').substring(0, 10)
                                : this.departure;
                            if (dep) {
                                this.updateFromPriceInDOM(this.arrival, dep, config.calendarData || {});
                            } else {
                                this.clearFromPrice();
                            }
                        } else {
                            this.clearFromPrice();
                        }
                        return;
                    }

                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                        if (datePicker.start && !datePicker.end) {
                            if (!this.isRangeMode) {
                                this.arrival = formatToISO(datePicker.start);
                                this.departure = this.arrival;
                                this.flexibleMonthLabel = null;
                                if (this.validateSelection()) {
                                    this.$emit('change', { arrival: this.arrival, departure: this.departure });
                                    datePicker.closeDatepicker();
                                } else {
                                    datePicker.start = false;
                                    datePicker.end = false;
                                    this.arrival = '';
                                    this.departure = '';
                                    document.querySelectorAll('.datepicker__month-day--selected').forEach(el => {
                                        el.classList.remove('datepicker__month-day--selected');
                                    });
                                }
                                return;
                            }

                            this.updateErrorVisibility(true);

                            const selectedCheckIn = formatToISO(datePicker.start);
                            const dayData = calendarData[selectedCheckIn];
                            let minNightsForThisDate = 1;

                            if (dayData?.restrictions?.hasMinLengthOfStay) {
                                minNightsForThisDate = parseInt(dayData.restrictions.restrictionUnitAmount);
                            }

                            datePicker.minNights = minNightsForThisDate;
                            datePicker.minDays = datePicker.minNights > 1 ? datePicker.minNights + 1 : 2;

                            datePicker.updateSelectableRange();
                            this.restorePreStartDaysWronglyInvalidatedByMinStay();
                            this.clearFromPrice();
                        }

                        if (datePicker.start && datePicker.end) {
                            this.arrival = formatToISO(datePicker.start);
                            this.departure = formatToISO(datePicker.end);
                            this.flexibleMonthLabel = null;
                            this.validateSelection();
                            this.updateFromPriceInDOM(this.arrival, this.departure, calendarData);
                            this.$emit('change', { arrival: this.arrival, departure: this.departure });
                        }
                        });
                    });
                },
            };
        },

        initPicker() {
            const input = document.getElementById('trigger');

            if (!input) {
                return;
            }

            this.config.datepickerConfig.calendarData = {};
            window.hotelDatePicker = new HotelDatepicker(input, this.getPickerConfig());
        },

        reinitDatePicker() {
            const datePicker = window.hotelDatePicker;
            if (!datePicker) {
                return;
            }

            const hasLiveInProgressSelection = this.isRangeMode && datePicker.start && !datePicker.end;
            if (!hasLiveInProgressSelection) {
                datePicker.minNights = this.config.datepickerConfig.minNights || 1;
                datePicker.minDays = datePicker.minNights > 1 ? datePicker.minNights + 1 : 2;

                const parsedStart = this.arrival ? datePicker.parseDate(this.arrival, datePicker.format) : false;
                const parsedEnd = this.departure ? datePicker.parseDate(this.departure, datePicker.format) : false;

                if (parsedStart && !isNaN(parsedStart)) {
                    datePicker.start = parsedStart.getTime();
                    if (!this.isRangeMode) {
                        datePicker.end = datePicker.start;
                    } else {
                        datePicker.end = (parsedEnd && !isNaN(parsedEnd)) ? parsedEnd.getTime() : false;
                    }
                }
            }

            this.addRestrictionsToDatePicker(datePicker, this.config.datepickerConfig.calendarData);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    try {
                        datePicker.checkAndSetDayClasses();
                        datePicker.showSelectedDays();
                        datePicker.setDayIndexes();
                        datePicker.setDayAriaAttributes();
                        this.enrichDayAriaLabels();
                        datePicker.disableNextPrevButtons();
                        if (datePicker.start && datePicker.end) {
                            datePicker.showSelectedInfo();
                        }
                    } catch (e) {}

                    try {
                        this.restorePreStartDaysWronglyInvalidatedByMinStay();
                        this.addMinStayDataToCells();
                        this.refreshCellData();
                    } catch (e) {}
                });
            });
        },

        refreshAvailability(fetchBothVisibleMonths = false) {
            let months = [];
            const datePicker = window.hotelDatePicker;

            if (!datePicker) {
                return;
            }

            if (!this.selectedHotel || this.selectedHotel == 0) {
                this.config.datepickerConfig.calendarData = {};

                this.reinitDatePicker();
                return;
            }

            if (fetchBothVisibleMonths && this.arrival) {
                months.push(spacetime(this.arrival).startOf('month').format('iso').substring(0, 10));
                months.push(spacetime(this.arrival).add(1, 'month').startOf('month').format('iso').substring(0, 10));
            } else {
                if (datePicker.month1) {
                    months.push(spacetime(datePicker.month1).format('iso').substring(0, 10));
                }

                if (!datePicker.isSingleMonth() && datePicker.month2) {
                    months.push(spacetime(datePicker.month2).format('iso').substring(0, 10));
                }
            }

            this.service.fetchAvailability(this.selectedHotel, months);
        },

        restorePreStartDaysWronglyInvalidatedByMinStay() {
            const datePicker = window.hotelDatePicker;
            if (!datePicker || !datePicker.start || datePicker.end) {
                return;
            }

            const calendarData = this.config.datepickerConfig.calendarData || {};
            const currentStartTimestamp = new Date(datePicker.start).setHours(0, 0, 0, 0);

            document.querySelectorAll('.datepicker__month-day--visibleMonth.datepicker__month-day--invalid.datepicker__month-day--tmp').forEach(dayEl => {
                const timestamp = Number(dayEl.getAttribute('time'));
                if (!timestamp || timestamp >= currentStartTimestamp) {
                    return;
                }

                const dayInfo = calendarData[formatToISO(timestamp)];
                if (!dayInfo || dayInfo.isAvailable === false) {
                    return;
                }

                const ownMinNights = dayInfo.restrictions?.hasMinLengthOfStay
                    ? parseInt(dayInfo.restrictions.restrictionUnitAmount)
                    : 1;
                const nightsIfUsedAsArrival = Math.round((currentStartTimestamp - timestamp) / 86400000);
                if (nightsIfUsedAsArrival < ownMinNights) {
                    return;
                }

                if (!this.service._checkIntervalConsistency(formatToISO(timestamp), nightsIfUsedAsArrival, calendarData)) {
                    return;
                }

                dayEl.classList.remove('datepicker__month-day--invalid', 'datepicker__month-day--tmp');
                dayEl.classList.add('datepicker__month-day--valid');
            });
        },

        updateMinStayForEffectiveArrival(evt) {
            if (evt.target?.tagName?.toLowerCase() !== 'td') {
                return;
            }

            if (evt.target.classList.contains('datepicker__month-day--invalid') || evt.target.classList.contains('datepicker__month-day--disabled')) {
                return;
            }

            const datePicker = window.hotelDatePicker;
            if (!datePicker || !datePicker.start || datePicker.end) {
                return;
            }

            const clickedTime = parseInt(evt.target.getAttribute('time'), 10);
            if (!clickedTime || clickedTime === datePicker.start) {
                return;
            }

            const effectiveArrivalTime = Math.min(datePicker.start, clickedTime);
            const effectiveDepartureTime = Math.max(datePicker.start, clickedTime);
            const calendarData = this.config.datepickerConfig.calendarData || {};
            const dayData = calendarData[formatToISO(effectiveArrivalTime)];
            const minNights = dayData?.restrictions?.hasMinLengthOfStay
                ? parseInt(dayData.restrictions.restrictionUnitAmount)
                : 1;

            const selectedNights = Math.round((effectiveDepartureTime - effectiveArrivalTime) / 86400000);
            if (selectedNights < minNights) {
                evt.preventDefault();
                evt.stopPropagation();
                return;
            }

            datePicker.minNights = minNights;
            datePicker.minDays = minNights > 1 ? minNights + 1 : 2;
        },

        matchDatepickerWidthToRow() {
            const datepickerEl = document.querySelector('.datepicker');
            const trigger = document.getElementById('trigger');
            const row = trigger?.closest('.row');
            if (!datepickerEl || !row) {
                return;
            }

            datepickerEl.style.width = row.getBoundingClientRect().width + 'px';
        },

        addRestrictionsToDatePicker(datePicker, calendarData) {
            const disabled = [], noCheckIn = [], noCheckOut = [];

            Object.keys(calendarData).forEach(date => {
                const day = calendarData[date], res = day.restrictions || {};
                if (!day.isAvailable) {
                    disabled.push(date);
                }
                if (res.isClosedForArrival) {
                    noCheckIn.push(date);
                }
                if (res.isClosedForDeparture) {
                    noCheckOut.push(date);
                }
            });

            datePicker.disabledDates = disabled;
            datePicker.noCheckInDates = noCheckIn;
            datePicker.noCheckOutDates = noCheckOut;
            datePicker.parseDisabledDates();
        },

        addMinStayDataToCells() {
            const calendarData = this.config.datepickerConfig.calendarData || {};
            const numNights = this.config.datepickerConfig.numNights || 1;

            document.querySelectorAll('.datepicker__month-day--visibleMonth').forEach(el => {
                const timestamp = el.getAttribute('time');
                if (!timestamp) return;

                const dateKey = formatToISO(timestamp);
                const dayInfo = calendarData[dateKey];

                el.classList.remove('datepicker__month-day--valid-min-stay');
                if (dayInfo?.restrictions?.hasMinLengthOfStay) {
                    el.classList.add('datepicker__month-day--valid-min-stay');
                    el.setAttribute('data-min-nights', dayInfo.restrictions.restrictionUnitAmount);
                }

                if (!this.isRangeMode) {
                    const requiredNights = dayInfo?.restrictions?.hasMinLengthOfStay
                        ? parseInt(dayInfo.restrictions.restrictionUnitAmount)
                        : 1;
                    if (requiredNights > numNights) {
                        el.classList.add('datepicker__month-day--invalid');
                    }
                }
            });
        },

        validateSelection() {
            const config = this.config.datepickerConfig;
            if (!config.calendarData || Object.keys(config.calendarData).length === 0) {
                this.updateErrorVisibility(true);
                return true;
            }

            let departure = this.departure;
            if (!this.isRangeMode) {
                const numNights = config.numNights || 1;
                departure = spacetime(this.arrival).add(numNights, 'day').format('iso').substring(0, 10);
            }

            const isValid = this.service.isRangeAvailable(
                this.selectedHotel,
                this.arrival,
                departure,
                config.minNights
            );

            this.updateErrorVisibility(isValid);
            return isValid;
        },

        updateErrorVisibility(isValid) {
            const errorElement = document.querySelector('.calendar-legend-error');
            if (errorElement) {
                errorElement.style.display = isValid ? 'none' : 'block';
            }
        },

        updateLegendVisibility() {
            const legendEl = document.getElementById('custom-html');
            if (legendEl) {
                legendEl.style.display = (!this.selectedHotel || this.selectedHotel == 0) ? 'none' : '';
            }
        },

        setupListenersForMonthButtons() {
            this._monthButtonHandler = (event) => {
                if (event.target.matches('.datepicker__month-button--next') || event.target.matches('.datepicker__month-button--prev')) {
                    this.refreshAvailability(false);
                    this.translateCalendarToArabic();
                }
            };
            document.addEventListener('click', this._monthButtonHandler);
        },

        handleResize() {
            requestAnimationFrame(() => {
                this.addMinStayDataToCells();
                const picker = window.hotelDatePicker;
                if (picker && picker.start && !picker.end) {
                    this.restorePreStartDaysWronglyInvalidatedByMinStay();
                }
                if (!this.isRangeMode && !this.enablePricing && this.isDatepickerOpen) {
                    this.matchDatepickerWidthToRow();
                }
            });
        },

        _getCurrencyNoticeText() {
            const currencyEntity = this.service.currencyEntityForNotice;
            if (!currencyEntity) return null;
            const template = this.config.datepickerConfig.i18n.price_currency_notice ?? 'Prices shown in %s';
            return template.replace('%s', currencyEntity);
        },

        getCustomHTML() {
            return renderComponentToHtml(CalendarLegend, { i18n: this.config.datepickerConfig.i18n || {} });
        },

        _calendarEl(selector) {
            return document.querySelector(`#custom-html ${selector}`);
        },

        updateCurrencyNoticeInDOM() {
            const noticeEl = this._calendarEl('.calendar-currency-notice');
            if (noticeEl) noticeEl.textContent = this._getCurrencyNoticeText() || '';
        },

        clearFromPrice() {
            const el = this._calendarEl('.calendar-from-price');
            if (el) el.textContent = '';
        },

        _calcRangeTotal(arrival, departure, calendarData) {
            let total = 0, sampleDay = null;
            let current = spacetime(arrival);
            const end = spacetime(departure);
            while (current.isBefore(end)) {
                const dayData = calendarData[current.format('iso').substring(0, 10)];
                if (dayData?.rawPrice) {
                    total += dayData.rawPrice;
                    sampleDay ??= dayData;
                }
                current = current.add(1, 'day');
            }
            return { total, sampleDay };
        },

        _formatFromPrice(sampleDay, total, arrival, departure) {
            const nights = Math.round(Math.abs(spacetime(arrival).diff(spacetime(departure), 'day')));
            const locale = document.documentElement.lang || window.currentLocale || 'en';
            const totalStr = arabicDigits(new Intl.NumberFormat(locale).format(Math.round(total)));
            const formattedWithCurrency = sampleDay.currencyCode
                ? attachCurrency(totalStr, sampleDay.currencyCode, sampleDay.displayPrice)
                : totalStr;
            const i18n = this.config.datepickerConfig.i18n;
            const nightText = nights === 1 ? (i18n.night ?? 'night') : (i18n.nights ?? 'nights');
            return (i18n.from_price ?? 'From %price% for %nights%')
                .replace('%price%', formattedWithCurrency)
                .replace('%nights%', `${arabicDigits(String(nights))} ${nightText}`);
        },

        updateFromPriceInDOM(arrival, departure, calendarData) {
            const el = this._calendarEl('.calendar-from-price');
            if (!el) return;

            if (!this.enablePricing) {
                el.textContent = '';
                return;
            }

            const { total, sampleDay } = this._calcRangeTotal(arrival, departure, calendarData);

            const noticeEl = this._calendarEl('.calendar-currency-notice');

            if (!sampleDay || total === 0) {
                el.textContent = '';
                return;
            }

            el.textContent = this._formatFromPrice(sampleDay, total, arrival, departure);
            if (noticeEl) noticeEl.textContent = '';
        },

        translateCalendarToArabic() {
            const locale = document.documentElement.lang || window.currentLocale;
            if (locale !== 'ar') return;

            const datepickerEl = document.querySelector('.datepicker');
            if (!datepickerEl) return;

            const days = datepickerEl.querySelectorAll('.datepicker__month-day--visibleMonth');
            days.forEach(day => {
                const textNode = Array.from(day.childNodes).find(node => node.nodeType === 3);
                if (textNode) {
                    const arabicDay = arabicDigits(textNode.textContent.trim());
                    if (textNode.textContent !== arabicDay) {
                        textNode.textContent = arabicDay;
                    }
                }
            });

            const headers = datepickerEl.querySelectorAll('.datepicker__month-name');
            headers.forEach(header => {
                header.innerText = arabicDigits(header.innerText);
            });
        },

        refreshCellData() {
            const config = this.config.datepickerConfig;
            const calendarData = config.calendarData || {};

            document.querySelectorAll('.datepicker__month-day--visibleMonth').forEach(el => {
                const timestamp = el.getAttribute('time');
                if (!timestamp) return;

                const dateKey = formatToISO(Number(timestamp));
                const dayData = calendarData[dateKey];

                if (dayData) {
                    this.updateCellPrice(el, dayData);
                    this.updateCellTooltip(el, dayData);
                } else {
                    const priceSpan = el.querySelector('.calendar-price');
                    if (priceSpan) priceSpan.innerHTML = '';

                    const tooltip = el.querySelector('.custom-datepicker__tooltip');
                    if (tooltip) tooltip.innerHTML = '';

                    el.classList.remove('has-special-events', 'has-calendar-event', 'tooltip-open');
                    const star = el.querySelector('.calendar-event-star');
                    if (star) star.remove();
                    el.removeAttribute('data-price');
                }
            });
        },

        updateCellPrice(el, dayData) {
            if (!this.enablePricing || !dayData.price) return;

            let priceSpan = el.querySelector('.calendar-price');
            if (!priceSpan) {
                priceSpan = document.createElement('span');
                priceSpan.className = 'calendar-price';
                el.appendChild(priceSpan);
            }
            const displayPrice = dayData.price;

            if (priceSpan.innerHTML !== displayPrice) {
                priceSpan.innerHTML = displayPrice;
            }

            if (dayData.rawPrice) {
                el.dataset.price = dayData.rawPrice;
            }
        },

        updateCellTooltip(el, dayData) {
            const tooltipContainer = el.querySelector('.custom-datepicker__tooltip');
            if (!tooltipContainer) return;

            const restrictionItems = this.getSpecialEventItems(dayData);
            const calendarEventItems = this.getCalendarEventItems(dayData);
            const allItems = [...restrictionItems, ...calendarEventItems];

            const html = this.getTooltipHtml(allItems);
            if (tooltipContainer.innerHTML !== html) {
                tooltipContainer.innerHTML = html;
            }

            el.classList.toggle('has-special-events', allItems.length > 0);

            const hasCalendarEvent = calendarEventItems.length > 0;
            el.classList.toggle('has-calendar-event', hasCalendarEvent);

            let star = el.querySelector('.calendar-event-star');
            if (hasCalendarEvent && !star) {
                star = document.createElement('span');
                star.className = 'calendar-event-star';
                star.setAttribute('aria-hidden', 'true');
                el.appendChild(star);
            } else if (!hasCalendarEvent && star) {
                star.remove();
                el.classList.remove('tooltip-open');
            }
        },

        getTooltipHtml(items) {
            if (items.length === 0) {
                return '';
            }

            const cacheKey = JSON.stringify(items);
            if (this._tooltipHtmlCache.has(cacheKey)) {
                return this._tooltipHtmlCache.get(cacheKey);
            }

            const html = renderComponentToHtml(CalendarTooltipList, { items });
            this._tooltipHtmlCache.set(cacheKey, html);
            return html;
        },

        handleEventTooltipClick(evt) {
            const cell = evt.target.closest?.('.datepicker__month-day.has-calendar-event');

            if (cell) {
                const wasOpen = cell.classList.contains('tooltip-open');
                document.querySelectorAll('.datepicker__month-day.tooltip-open').forEach(el => el.classList.remove('tooltip-open'));
                if (!wasOpen) cell.classList.add('tooltip-open');
                return;
            }

            document.querySelectorAll('.datepicker__month-day.tooltip-open').forEach(el => el.classList.remove('tooltip-open'));
        },

        getSpecialEventItems(dayData) {
            if(!dayData.isAvailable) {
                return [];
            }

            const translations = this.config.datepickerConfig.i18n || {};
            const items = [];
            const res = dayData?.restrictions || {};

            if (res.isClosedForArrival) {
                items.push({ className: 'no-checkin', text: translations.no_checkin_tooltip || 'No check-in on this date' });
            }
            if (res.isClosedForDeparture) {
                items.push({ className: 'no-checkout', text: translations.no_checkout_tooltip || 'No check-out on this date' });
            }
            if (res.hasMinLengthOfStay) {
                const nightsCount = arabicDigits(res.restrictionUnitAmount.toString());
                const minStayLabel = translations.min_stay_tooltip
                    ? translations.min_stay_tooltip.replace('%s', nightsCount)
                    : `Min. stay: ${nightsCount} nights`;

                items.push({ className: 'min-stay-event', text: minStayLabel });
            }

            return items;
        },

        getCalendarEventItems(dayData) {
            return (dayData?.events || []).map(title => ({ className: 'calendar-event-item', text: title }));
        },

        enrichDayAriaLabels() {
            const calendarData = this.config.datepickerConfig.calendarData || {};
            const translations = this.config.datepickerConfig.i18n || {};

            document.querySelectorAll('.datepicker__month-day--visibleMonth').forEach(el => {
                const timestamp = el.getAttribute('time');
                if (!timestamp) return;

                const dateKey = formatToISO(Number(timestamp));
                const dayData = calendarData[dateKey];
                if (!dayData) return;

                const res = dayData.restrictions || {};
                const extra = [];

                if (res.hasMinLengthOfStay) {
                    const n = res.restrictionUnitAmount;
                    extra.push(translations.min_stay_tooltip
                        ? translations.min_stay_tooltip.replace('%s', n)
                        : `Min. stay: ${n} nights`);
                }
                if (res.isClosedForArrival) {
                    extra.push(translations.no_checkin_tooltip || 'No check-in on this date');
                }
                if (res.isClosedForDeparture) {
                    extra.push(translations.no_checkout_tooltip || 'No check-out on this date');
                }
                (dayData.events || []).forEach(title => extra.push(title));

                if (extra.length === 0) return;

                const current = el.getAttribute('aria-label') || '';
                if (extra.some(e => current.includes(e))) return;

                el.setAttribute('aria-label', `${current}. ${extra.join('. ')}`);
            });
        },

        initCalendarObserver() {
            if (this.calendarObserver) {
                return;
            }

            this.calendarObserver = new MutationObserver((mutations) => {
                const isInternalChange = mutations.some(m =>
                    m.target.classList.contains('calendar-price') ||
                    m.target.classList.contains('custom-datepicker__tooltip')
                );

                if (isInternalChange) {
                    return
                };

                const hasNewElements = mutations.some(m => {
                    return m.addedNodes.length > 0
                });

                if (!hasNewElements) {
                    return;
                }

                if (this.refreshTimeout) {
                    cancelAnimationFrame(this.refreshTimeout);
                }

                this.refreshTimeout = requestAnimationFrame(() => {
                    this.calendarObserver.disconnect();
                    this.refreshCellData();
                    this.reconnectObserver();
                    this.refreshTimeout = null;
                });
            });

            this.reconnectObserver();
        },

        reconnectObserver() {
            if (!this.calendarObserver) {
                return;
            }

            const target = document.querySelector('.datepicker') || document.body;
            if (target) {
                this.calendarObserver.observe(target, {
                        childList: true,
                        subtree: true
                    });
            }
        }
    },
}
</script>
